import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { APPLICATION_TYPES } from '@/mocks/dealer';
import { COUNTRIES } from '@/mocks/countries';
import { submitDealerApplication } from '@/lib/dealerApplication';
import type { ApplicationType } from '@/types/domain';

interface DealerApplicationFormProps {
  applicationType: ApplicationType | '';
  onChangeApplicationType: (value: ApplicationType | '') => void;
}

const inputClass =
  'w-full rounded-md border border-foreground-700 bg-foreground-900/70 px-4 py-3 text-sm text-background-50 placeholder:text-background-100/40 transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';

const labelClass = 'text-sm font-medium text-background-100/80';

export default function DealerApplicationForm({
  applicationType,
  onChangeApplicationType,
}: DealerApplicationFormProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus('idle');

    const form = e.currentTarget;
    const result = await submitDealerApplication(form);

    if (result.ok) {
      setStatus('success');
      form.reset();
      setCharCount(0);
      onChangeApplicationType('');
    } else {
      setStatus('error');
      setErrorMsg(result.message || t('dealer.form.error'));
    }
    setSubmitting(false);
  };

  return (
    <section id="basvuru" className="bg-foreground-950 py-20 md:py-28">
      <div className="mx-auto max-w-4xl px-4 md:px-6">
        <div className="reveal mx-auto max-w-2xl text-center">
          <span className="mb-5 inline-block h-px w-12 bg-primary-500"></span>
          <h2 className="font-heading text-3xl leading-tight text-background-50 md:text-4xl lg:text-5xl">
            {t('dealer.form.title')}
          </h2>
          <p className="mt-5 text-sm leading-relaxed text-background-100/70 md:text-base">
            {t('dealer.form.subtitle')}
          </p>
        </div>

        <form data-readdy-form onSubmit={handleSubmit} className="reveal reveal-delay-1 mt-12" noValidate>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <label htmlFor="app-name" className={labelClass}>
                {t('dealer.form.name')}
              </label>
              <input id="app-name" name="name" type="text" required placeholder={t('dealer.form.name')} className={inputClass} />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="app-company" className={labelClass}>
                {t('dealer.form.company')}
              </label>
              <input id="app-company" name="company" type="text" placeholder={t('dealer.form.company')} className={inputClass} />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="app-phone" className={labelClass}>
                {t('dealer.form.phone')}
              </label>
              <input id="app-phone" name="phone" type="tel" required placeholder="+90 5xx xxx xx xx" className={inputClass} />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="app-email" className={labelClass}>
                {t('dealer.form.email')}
              </label>
              <input id="app-email" name="email" type="email" required placeholder="ornek@email.com" className={inputClass} />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="app-country" className={labelClass}>
                {t('dealer.form.country')}
              </label>
              <select id="app-country" name="country" className={inputClass} defaultValue="">
                <option value="" disabled>
                  {t('dealer.form.selectPlaceholder')}
                </option>
                {COUNTRIES.map((country) => (
                  <option key={country} value={country} className="text-foreground-950">
                    {country}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="app-city" className={labelClass}>
                {t('dealer.form.city')}
              </label>
              <input id="app-city" name="city" type="text" placeholder={t('dealer.form.city')} className={inputClass} />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="app-website" className={labelClass}>
                {t('dealer.form.website')}
              </label>
              <input id="app-website" name="website" type="text" placeholder="https://" className={inputClass} />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="app-instagram" className={labelClass}>
                {t('dealer.form.instagram')}
              </label>
              <input id="app-instagram" name="instagram" type="text" placeholder="@kullanici" className={inputClass} />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="app-field" className={labelClass}>
                {t('dealer.form.field')}
              </label>
              <input id="app-field" name="field" type="text" placeholder={t('dealer.form.field')} className={inputClass} />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="app-type" className={labelClass}>
                {t('dealer.form.applicationType')}
              </label>
              <select
                id="app-type"
                name="application_type"
                className={inputClass}
                value={applicationType}
                onChange={(e) => onChangeApplicationType(e.target.value as ApplicationType | '')}
              >
                <option value="" disabled>
                  {t('dealer.form.selectPlaceholder')}
                </option>
                {APPLICATION_TYPES.map((opt) => (
                  <option key={opt.value} value={opt.value} className="text-foreground-950">
                    {t(opt.labelKey)}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2 sm:col-span-2">
              <label htmlFor="app-message" className={labelClass}>
                {t('dealer.form.howToWork')}
              </label>
              <textarea
                id="app-message"
                name="message"
                rows={4}
                maxLength={500}
                placeholder={t('dealer.form.messagePh')}
                className={`${inputClass} resize-none`}
                onChange={(e) => setCharCount(e.target.value.length)}
              ></textarea>
              <span className="text-xs text-background-100/40">{charCount}/500</span>
            </div>

            <div className="field-supplemental">
              <input
                type="text"
                name="contact_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
            </div>
          </div>

          {status === 'success' && (
            <div className="mt-6 flex items-center gap-2 rounded-md border border-primary-500/40 bg-primary-500/10 px-4 py-3 text-sm text-primary-300">
              <i className="ri-checkbox-circle-line text-lg" aria-hidden="true"></i>
              {t('dealer.form.success')}
            </div>
          )}
          {status === 'error' && (
            <div className="mt-6 flex items-center gap-2 rounded-md border border-accent-500/50 bg-accent-500/10 px-4 py-3 text-sm text-accent-200">
              <i className="ri-error-warning-line text-lg" aria-hidden="true"></i>
              {errorMsg}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="mt-8 w-full whitespace-nowrap rounded-md bg-primary-500 px-8 py-4 text-sm font-semibold uppercase tracking-wider text-foreground-950 transition-colors hover:bg-primary-400 disabled:opacity-60 cursor-pointer"
          >
            {submitting ? (
              <i className="ri-loader-4-line" aria-hidden="true"></i>
            ) : (
              t('dealer.form.submit')
            )}
          </button>
        </form>
      </div>
    </section>
  );
}